import React, { useState, useEffect, useRef } from "react";
import { useLookupStore } from "../stores/lookupStore";

/** Minimum time (ms) to show loading skeleton to prevent flashing */
const MIN_LOADING_TIME_MS = 1000;

interface FlehsPageProps {
  /** Optional text displayed above the main title */
  preTitle?: string;
  /** Main page title */
  title: React.ReactNode;
  /** Loading title - shown while loading instead of main title */
  loadingTitle?: React.ReactNode;
  /** Action buttons or links for the page header */
  actions?: React.ReactNode;
  /** Loading state */
  loading?: boolean;
  /** Custom loading placeholder to display while loading */
  loadingPlaceholder?: React.ReactNode;
  /** Page content */
  children: React.ReactNode;
  /** Additional CSS classes for the page container */
  className?: string;
}

export const FlehsPage: React.FC<FlehsPageProps> = ({
  preTitle,
  title,
  loadingTitle,
  actions,
  loading = false,
  loadingPlaceholder,
  children,
  className = "",
}) => {
  // Check if lookups are still loading
  const lookupsLoading = useLookupStore((state) => state.progress.isLoading);

  // Combined loading state: show skeleton if page is loading OR lookups are loading
  const isLoading = loading || lookupsLoading;

  // Minimum loading time to prevent skeleton from flashing
  const [minTimeElapsed, setMinTimeElapsed] = useState(!isLoading);
  const timerRef = useRef<number>(undefined);
  useEffect(() => {
    if (isLoading) {
      setMinTimeElapsed(false);
      clearTimeout(timerRef.current);
      timerRef.current = window.setTimeout(
        () => setMinTimeElapsed(true),
        MIN_LOADING_TIME_MS
      );
    }
  }, [isLoading]);
  useEffect(() => () => clearTimeout(timerRef.current), []);

  // Show loading until both: data is loaded AND minimum time has elapsed
  const showLoading = isLoading || !minTimeElapsed;

  // Use loadingTitle while showing loading state (synced with skeleton visibility)
  const displayTitle = showLoading && loadingTitle ? loadingTitle : title;

  return (
    <div className={className}>
      {/* Page Header using Tabler classes */}
      <div className="page-header d-print-none">
        <div className="row g-2 align-items-center">
          <div className="col-12 col-md">
            {preTitle && <div className="page-pretitle">{preTitle}</div>}
            <h2 className="page-title">{displayTitle}</h2>
          </div>
          {actions && (
            <div className="col-12 col-md-auto d-print-none">
              <div className="btn-list">{actions}</div>
            </div>
          )}
        </div>
      </div>

      {/* Page Body - Content or Loading */}
      <div className="page-body">
        {showLoading && loadingPlaceholder ? (
          <div className="placeholder-glow">{loadingPlaceholder}</div>
        ) : (
          children
        )}
      </div>
    </div>
  );
};
